import { message, Row, Typography } from "antd";
import { t } from "i18next";
import React from "react";
import applePayIcon from "../../assets/icons/applePay3.svg";
import { useApplePay } from "../../helpers/hooks/useApplePay";
import { validateEmail } from "../../utls/FunctionsUtil";
import { generateGuid } from "../../utls/generateGuid";
import "./index.less";

interface ApplePayButtonProps {
  paymentAmount: any;
  title: any;
  currentUser: any;
  type: any;
  handlePayment: any;
  email?: string | null;
}

const TheApplePayButton: React.FC<ApplePayButtonProps> = ({ paymentAmount, title, currentUser, type, handlePayment, email }) => {
  const applePayReady = useApplePay();

  const API_KEY = process.env.REACT_APP_PAY_SAFE_API_KEY;
  const paySafeValidateURL = process.env.REACT_APP_PAY_SAFE_VALIDATE_URL;
  const MERCHANT_ID = process.env.REACT_APP_PAY_SAFE_MERCHANT_ID;
  const MANAGEMENT_URL = process.env.REACT_APP_PAY_SAFE_MANAGEMENT_URL;
  const TOKEN_NOTIFICATION_URL = process.env.REACT_APP_TOKEN_NOTIFICATION_URL;

  const merchantRefNum = generateGuid();
  const totalAmount = Number(paymentAmount());
  const amount = Number(totalAmount?.toFixed(2));
  const label = title();

  const log = (message: any) => {
    const outputElement = document.getElementById("output");
    if (outputElement) {
      outputElement.innerHTML += `<br>[${new Date().toLocaleTimeString()}] ${JSON.stringify(message)}`;
    }
  };

  const getOptions = () => {
    log(`Amount to be charged: ${amount}`);
    log(`Generated Merchant Reference Number: ${merchantRefNum}`);

    return {
      amount: amount,
      countryCode: "US",
      currencyCode: "EUR",
      merchantCapabilities: ["supports3DS", "supportsDebit", "supportsCredit"],
      requiredBillingContactFields: ["postalAddress", "name", "email"],
      supportedNetworks: ["visa", "masterCard", "amex", "discover"],
      total: {
        label: label,
        amount,
      },
      transactionType: "PAYMENT",
      paymentType: "APPLEPAY",
      merchantRefNum,
      applePay: {
        requireBillingAddress: true,
        recurringPaymentRequest: {
          paymentDescription: label,
          regularBilling: {
            label: label,
            amount,
            paymentTiming: "recurring",
          },
          managementURL: MANAGEMENT_URL,
          tokenNotificationURL: `${TOKEN_NOTIFICATION_URL}/${MERCHANT_ID}`,
        },
      },
      customerDetails: {
        profile: {
          firstName: currentUser?.infos?.firstName, // These can be dynamic, assuming they are available
          lastName: currentUser?.infos?.lastName,
          email: currentUser?.infos?.privateInfos?.email,
        },
      },
    };
  };

  const handleApplePay = () => {
    if (!applePayReady) return;

    //SHOW THE WARNING POPUP BEFORE THE APPLE PAY MODEL
    if (email && !validateEmail(email) && type === "decrypt") {
      return message.warning(t("email-invalid"));
    }

    const options = getOptions();

    //NOTE: 3 STAND TO THE VERSION ON APPLE PAY JS API (3 FOR CURRENT AND STABLE VERSION)
    const session = new window.ApplePaySession(3, options);

    //NOTE: VALIDATE MERCHANT
    session.onvalidatemerchant = async (event) => {
      function getApplePaySession(url: string) {
        return new Promise(function (resolve, reject) {
          var xhr = new XMLHttpRequest();

          xhr.open("POST", paySafeValidateURL!);

          xhr.onload = function () {
            try {
              if (this.status >= 200 && this.status < 300) {
                resolve(JSON.parse(xhr.response));

                session.completeMerchantValidation(JSON.parse(xhr.response));
              } else {
                reject({
                  status: this.status,
                  statusText: xhr.statusText,
                });
              }
            } catch (error) { }
          };
          xhr.onerror = function () {
            reject({
              status: this.status,
              statusText: xhr.statusText,
            });
          };
          xhr.setRequestHeader("Content-Type", "application/json");
          xhr.setRequestHeader("X-Paysafe-Credentials", "Basic " + API_KEY);
          xhr.send(
            JSON.stringify({
              validationUrl: url,
              domainName: window.location.host,
              displayName: "Universal Media",
            })
          );
        });
      }
      try {
        await getApplePaySession(event.validationURL);
      } catch (error) { }
    };

    //NOTE: PAYMENT AUTHORIZED
    session.onpaymentauthorized = async (event) => {
      // Access billing contact
      const billingContact = event?.payment?.billingContact;

      const billingDetails = {
        street: billingContact?.addressLines?.[0] || "",
        city: billingContact?.locality || "",
        zip: billingContact?.postalCode || "",
        country: billingContact?.countryCode || "",
        state: billingContact?.administrativeArea || "",
      };
      try {
        await handlePayment(billingDetails, event.payment);

        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
      } catch (error) {
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
      }
    };

    session.begin();
  };

  if (!applePayReady) {
    return <></>;
  }

  return (
    <>
      <div
        id="apple-pay"
        style={{
          position: "absolute",
          zIndex: "-100",
          left: "-1000px",
        }}
      ></div>

      <button className="applePayBtn" onClick={handleApplePay}>
        <img src={applePayIcon} alt="Apple Pay" width="45px" />
      </button>

      <Row className="mt-20 mb-20 justify-content-center">
        <Typography.Text className="text-white-color font-18-bold">{t("auth-signUp-divider")}</Typography.Text>
      </Row>
    </>
  );
};

export default TheApplePayButton;
